import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	type ActionFunctionArgs,
	json,
	type MetaFunction,
} from '@remix-run/node'
import { Form } from '@remix-run/react'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { createToastHeaders } from '#app/utils/toast.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'

export const meta: MetaFunction = () => [{ title: 'Breeze' }]

export default function Index() {
	return (
		<main className="container grid xl:min-h-[calc(100vh-96px)] place-items-center bg-white">
			<div className="grid px-4 py-16 xl:grid-cols-2 xl:gap-24">
				<div className="flex flex-col items-center gap-8 text-center xl:items-start xl:text-left">
					<h1
						data-heading
						className="grid animate-slide-top gap-1.5 text-4xl font-semibold text-foreground md:text-5xl xl:mt-4 xl:animate-slide-left xl:text-6xl"
					>
						<div className="[animation-delay:0.4s] [animation-fill-mode:backwards]">
							Hi I'm Mia. Your
						</div>
						<div className="bg-gradient-to-r from-red-600 via-indigo-600 to-indigo-600 bg-clip-text text-transparent [animation-delay:0.5s] [animation-fill-mode:backwards]">
							AI-Powered
						</div>
						<div className="[animation-delay:0.6s] [animation-fill-mode:backwards]">
							CRE Loan Processor
						</div>
					</h1>
					<p
						data-paragraph
						className="max-w-xl animate-slide-top text-xl/7 text-muted-foreground [animation-delay:0.8s] [animation-fill-mode:backwards] xl:animate-slide-left xl:text-xl/6 xl:leading-8"
					>
						I work 24/7 to review loan applications, run background checks, and
						prepare documents to help you safely underwrite loans faster.
					</p>
					<Waitlist />
					<YCombinator />
				</div>
				<Mia />
			</div>
		</main>
	)
}

function YCombinator() {
	return (
		<div className="flex animate-slide-top flex-col gap-y-1.5 [animation-delay:1.2s] [animation-fill-mode:backwards]">
			<div className="text-xs uppercase tracking-wide text-muted-foreground">
				Backed by
			</div>
			<span className="mt-1 block dark:hidden">
				<img src="/img/yc.svg" alt="YC logo" className="h-5" />
			</span>
			<span className="hidden items-center gap-1.5 dark:flex">
				<img src="/img/yc-dark.svg" alt="YC logo" className="h-5 w-5" />
				<span className="caption text-center">Combinator</span>
			</span>
		</div>
	)
}

const WaitlistSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = await parseWithZod(formData, {
		schema: WaitlistSchema,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const response = await fetch('https://api.getwaitlist.com/api/v1/signup', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			email: submission.value.email,
			waitlist_id: 19513,
		}),
	})

	if (response.ok) {
		const toastHeaders = await createToastHeaders({
			title: 'Success!',
			description: 'We will be in touch within 24 hours.',
			type: 'success',
		})
		return json({ status: 'success' } as const, { headers: toastHeaders })
	} else {
		const toastHeaders = await createToastHeaders({
			title: 'Error',
			description: 'Try again later.',
			type: 'error',
		})
		return json({ status: 'success' } as const, { headers: toastHeaders })
	}
}

function Waitlist() {
	const [form, fields] = useForm({
		id: 'join-waitlist',
		constraint: getZodConstraint(WaitlistSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: WaitlistSchema })
		},
	})
	const isPending = useIsPending()

	return (
		<Form
			method="POST"
			{...getFormProps(form)}
			className="animate-slide-top [animation-delay:1.0s] [animation-fill-mode:backwards]"
		>
			<fieldset
				disabled={isPending}
				className="flex flex-col gap-2 sm:flex-row"
			>
				<Field
					labelProps={{}}
					inputProps={{
						...getInputProps(fields.email, { type: 'email' }),
						className: 'w-64',
						autoComplete: 'email',
						placeholder: 'Your work email',
					}}
					errors={fields.email.errors}
				/>
				<StatusButton status={isPending ? 'pending' : (form.status ?? 'idle')}>
					Hire Mia
				</StatusButton>
			</fieldset>
			<ErrorList id={form.errorId} errors={form.errors} />
		</Form>
	)
}

function Mia() {
	const videoRef = useRef<HTMLVideoElement>(null)

	useEffect(() => {
		// Ensures the video starts playing automatically if it's not doing so
		if (videoRef.current) {
			videoRef.current.play().catch((error) => {
				console.error('Error trying to play the video:', error)
			})
		}
	}, [])

	return (
		<div className="mt-16 flex h-full w-auto items-center md:mt-8 lg:mt-4 overflow-hidden">
			<video ref={videoRef} loop autoPlay muted className="xl:scale-150 transform">
				<source src="/beggint-1.mov" type="video/mp4" />
				Your browser does not support the video tag.
			</video>
		</div>
	)
}
